/**
 * Historian results table (djin-cloud#806, task 18).
 *
 * Lifted from genieos `web/src/pages/HistorianPage.tsx:404-677` — the table
 * BODY only. Loading/error/empty-selection states are handled one level up:
 * the page shell renders this component only when `state.queryResult`
 * exists, so those states never reach here.
 *
 * Ported as-is: the windowed `react-window` body (a raw-interval result can
 * be tens of thousands of rows — genieos measured ~18,000 for a 5-minute
 * synchrophasor pull — and a `.map()` there stalls the tab before it can
 * paint), and the full ARIA table role set (`table` / `rowgroup` / `row` /
 * `columnheader` / `cell`). ⚠️ Ruling AG's correction: those roles already
 * exist in the source and MUST be preserved rather than reinvented — a
 * `FixedSizeList` renders plain `div`s with no implicit table semantics, so
 * a rewrite that drops them fails silently for a screen reader and for any
 * `getByRole('columnheader')` assertion.
 *
 * NOT ported: `queryLoading`/`queryError`/`executeQuery`/the "select
 * variables" empty card — this component receives already-resolved
 * `rows`/`columns` from the page shell and renders unconditionally.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { AlertCircle, Table2 } from 'lucide-react';
import { FixedSizeList as List } from 'react-window';
import { Card } from '@/components/gamma';
import { cn } from '@/utils/cn';
import type { SelectedVariable, TimeRange } from '@/contexts/HistorianContext';
import { formatHistorianValue, makeHistorianTimestampFormatter } from '@/utils/historianTableFormat';

/**
 * Results-table geometry. Fixed rather than content-derived because rows are
 * windowed: react-window needs a row height up front, and a header laid out
 * independently of the rows must agree with them on column widths or it
 * drifts as you scroll. Same values as genieos's `HistorianPage.tsx`.
 */
const ROW_HEIGHT_PX = 36;
const TIMESTAMP_COL_PX = 220; // fits "9/6/2026, 10:56:25.123 AM"
const VALUE_COL_PX = 160;

/**
 * Fallback for the measured height of the whole table BODY (header row plus
 * windowed rows) before a real measurement exists. A zero height renders an
 * empty list that looks exactly like "no data", so the fallback must be a
 * usable number rather than 0 — `src/test/setup.ts` mocks ResizeObserver as
 * a no-op, so its callback never fires in jsdom.
 */
const FALLBACK_LIST_HEIGHT_PX = 360;

/**
 * A window short enough that sub-second resolution could plausibly matter.
 *
 * genieos gates millisecond display on a server-reported
 * `HIGH_SPEED_INTERVAL` flag for an uncompressed PMU/raw pull — this repo has
 * no TDengine and no such flag on `/series-query`. The nearest available
 * signal is the selected time range itself: a multi-hour-or-longer pull is
 * downsampled server-side into minute-or-coarser buckets (`interval=auto`
 * resolves onto the `1m`-`6h` ladder within the range guard), and forcing
 * milliseconds on it would render sixty consecutive rows of an identical
 * ".000" — exactly the noise the raw-vs-trend distinction in
 * `makeHistorianTimestampFormatter`'s own doc comment warns about.
 */
const HIGH_RESOLUTION_RANGE_MS = 5 * 60 * 1000;

/** Stable empty array so a fresh render doesn't give `Row` a new identity for no reason. */
const EMPTY_ROWS: Array<Record<string, number | null>> = [];

/**
 * Measures a flex-sized box so react-window can be given a pixel height.
 * Same technique as genieos's `HistorianPage.tsx` — the table sits in a
 * `flex-1 min-h-0` box whose size is only known after layout.
 */
function useMeasuredHeight<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [measured, setMeasured] = useState(0);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect?.height;
      if (typeof next === 'number') setMeasured(next);
    });
    observer.observe(el);
    setMeasured(el.getBoundingClientRect().height);
    return () => observer.disconnect();
  }, []);

  return { ref, height: measured > 0 ? measured : FALLBACK_LIST_HEIGHT_PX };
}

/**
 * react-window's scrolling inner element, carrying the `rowgroup` role.
 * react-window owns this div, so without this the ARIA table would have an
 * unlabelled element between `table` and its `row`s.
 */
const TableRowGroup = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  (props, ref) => <div ref={ref} role="rowgroup" {...props} />
);
TableRowGroup.displayName = 'HistorianTableRowGroup';

export interface HistorianTableProps {
  columns: string[];
  rows: Array<Record<string, number | null>>;
  variables: SelectedVariable[];
  timeRange: TimeRange;
}

export const HistorianTable: React.FC<HistorianTableProps> = ({
  columns,
  rows: rowsProp,
  variables,
  timeRange,
}) => {
  const rows = rowsProp ?? EMPTY_ROWS;
  const { ref: bodyRef, height: bodyHeight } = useMeasuredHeight<HTMLDivElement>();

  const totalWidth = TIMESTAMP_COL_PX + Math.max(0, columns.length - 1) * VALUE_COL_PX;
  const listHeight = Math.max(ROW_HEIGHT_PX, bodyHeight - ROW_HEIGHT_PX);

  const withMillis =
    timeRange.end.getTime() - timeRange.start.getTime() <= HIGH_RESOLUTION_RANGE_MS;
  const formatTimestampCell = useMemo(
    () => makeHistorianTimestampFormatter(withMillis),
    [withMillis]
  );

  const Row = useCallback(
    ({ index, style }: { index: number; style: React.CSSProperties }) => {
      const row = rows[index];
      return (
        <div
          role="row"
          aria-rowindex={index + 2}
          style={style}
          className="flex items-center border-b border-ems-border/50 hover:bg-ems-bg-card-hover transition-colors"
        >
          {columns.map((col, colIdx) => (
            <div
              key={col}
              role="cell"
              aria-colindex={colIdx + 1}
              className={cn(
                'px-4 flex-shrink-0 truncate',
                colIdx === 0
                  ? 'text-ems-text-secondary font-mono text-xs'
                  : 'text-ems-text-primary'
              )}
              style={{ width: colIdx === 0 ? TIMESTAMP_COL_PX : VALUE_COL_PX }}
            >
              {col === 'timestamp' && typeof row?.[col] === 'number'
                ? formatTimestampCell(row[col] as number)
                : formatHistorianValue(row?.[col])}
            </div>
          ))}
        </div>
      );
    },
    [rows, columns, formatTimestampCell]
  );

  // Fix round 1 (task-18-review.md M1): the header row — and its ARIA role
  // set — used to vanish entirely on an empty result, behind an early
  // `return` into a bare "No data found" card. Task 19's
  // `getByRole('columnheader')` assertion failed there too, and a
  // screen-reader user lost all column context exactly when they need it
  // most (an empty result still names what was queried). The header now
  // renders UNCONDITIONALLY; only the body — the windowed list vs. the "no
  // data" message — is conditional on `rows.length`.
  return (
    <Card variant="default" className="h-full flex flex-col">
      <div className="p-3 border-b border-ems-border flex items-center gap-2 flex-shrink-0">
        <Table2 className="w-4 h-4 text-ems-blue" aria-hidden="true" />
        <span className="text-xs text-ems-text-secondary" aria-label={`${rows.length} rows`}>
          {rows.length.toLocaleString()} rows
        </span>
      </div>

      <div
        ref={bodyRef}
        className="flex-1 min-h-0 overflow-x-auto"
        role="region"
        aria-label="Query results table"
      >
        <div
          role="table"
          aria-rowcount={rows.length + 1}
          aria-colcount={columns.length}
          className="text-sm"
          style={{ minWidth: totalWidth }}
        >
          {/* Header row */}
          <div role="rowgroup" className="bg-ems-bg-card">
            <div
              role="row"
              aria-rowindex={1}
              className="flex items-center border-b border-ems-border"
              style={{ height: ROW_HEIGHT_PX }}
            >
              {columns.map((col, idx) => {
                const variable = variables.find((v) => v.alias === col);
                return (
                  <div
                    key={col}
                    role="columnheader"
                    aria-colindex={idx + 1}
                    className="px-4 py-2 text-left font-medium text-ems-text-secondary flex-shrink-0"
                    style={{ width: idx === 0 ? TIMESTAMP_COL_PX : VALUE_COL_PX }}
                  >
                    <div className="flex items-center gap-2">
                      {variable && (
                        <div
                          className="w-2 h-2 rounded-full flex-shrink-0"
                          style={{ backgroundColor: variable.color }}
                          aria-hidden="true"
                        />
                      )}
                      <span className="truncate">{variable?.displayLabel ?? col}</span>
                      {variable?.unit && (
                        <span className="text-xs opacity-60 flex-shrink-0">({variable.unit})</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {rows.length === 0 ? (
            /* Wrapped in rowgroup/row/cell: `status` is not an allowed child
               role of `table`, so an empty state placed directly under it made
               the grid structurally invalid exactly when a screen-reader user
               most needs to know why it is empty. */
            <div role="rowgroup">
              <div role="row">
                <div
                  role="cell"
                  aria-colspan={columns.length}
                  className="text-center py-12 text-ems-text-secondary"
                >
                  <div role="status">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" aria-hidden="true" />
                    <p>No data found for the selected time range</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <List
              height={listHeight}
              width="100%"
              itemCount={rows.length}
              itemSize={ROW_HEIGHT_PX}
              overscanCount={8}
              innerElementType={TableRowGroup}
            >
              {Row}
            </List>
          )}
        </div>
      </div>
    </Card>
  );
};

export default HistorianTable;
